import React, { useState, useEffect, useRef } from 'react';
import { X, Layers, Trash2, Calendar } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const CreateFromTemplateModal = ({
  isOpen,
  onClose,
  templates = [],
  onLoadTemplates,
  onCreate,
  onDeleteTemplate,
  isSubmitting = false,
}) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roles && Array.isArray(currentUser.roles) &&
    (currentUser.roles.includes(1) || currentUser.roles.some((r) => r.id === 1));

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [error, setError] = useState(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId(null);
      setName('');
      setDescription('');
      setStartDateTime('');
      setEndDateTime('');
      setError(null);
      if (onLoadTemplates) onLoadTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTemplateId) {
      const t = templates.find((tt) => tt.id === selectedTemplateId);
      if (t) {
        setName(t.name);
        setDescription(t.description || '');
        setTimeout(() => nameInputRef.current?.focus(), 50);
      }
    }
  }, [selectedTemplateId, templates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTemplateId) {
      setError('Pick a template first');
      return;
    }
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    if (startDateTime && endDateTime && new Date(endDateTime) <= new Date(startDateTime)) {
      setError('End datetime must be after start datetime');
      return;
    }
    setError(null);
    try {
      await onCreate(selectedTemplateId, {
        name: name.trim(),
        description,
        start_datetime: startDateTime || undefined,
        end_datetime: endDateTime || undefined,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create project from template');
    }
  };

  const handleDelete = async (e, templateId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await onDeleteTemplate(templateId);
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null);
      }
    } catch (err) {
      // notification handled by hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center space-x-2">
            <Layers className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Create Project from Template</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Pick a template</label>
            {templates.length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400 border border-dashed border-slate-700 rounded-lg">
                No templates yet. Save an existing project as a template first.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto pr-1 custom-scrollbar">
                {templates.map((t) => {
                  const isSelected = selectedTemplateId === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTemplateId(t.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-blue-900/30 border-blue-500/60'
                          : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white truncate">{t.name}</div>
                          {t.description && (
                            <div className="text-xs text-slate-400 mt-1 line-clamp-2">{t.description}</div>
                          )}
                          <div className="text-xs text-slate-500 mt-2">
                            {t.task_count || 0} task{t.task_count === 1 ? '' : 's'}
                            {t.created_by_name && ` · by ${t.created_by_name}`}
                          </div>
                        </div>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => handleDelete(e, t.id)}
                            title="Delete template"
                            className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedTemplateId && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">New project name</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Start (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={startDateTime}
                    onChange={(e) => setStartDateTime(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    End (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={endDateTime}
                    onChange={(e) => setEndDateTime(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500">
                All tasks from the template will be inserted unassigned, not completed, with no dates.
              </p>
            </>
          )}

          {error && <div className="text-red-400 text-sm">{error}</div>}

          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-300 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedTemplateId || !name.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg transition-colors text-sm"
            >
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFromTemplateModal;
